import type {
  ButtonHTMLAttributes,
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from "react";
import { Icon } from "./Icon";

// §11 Design-System — kleine, theme-getriebene Primitives (Tailwind-Tokens). Bewusst
// schlank: ein konsistenter Satz, den Shell + ToolUIs (SettingsUI) gemeinsam nutzen.

function cx(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(" ");
}

// ── Button ────────────────────────────────────────────────────────────────────
type ButtonVariant = "primary" | "secondary" | "ghost" | "danger";
type ButtonSize = "sm" | "md" | "lg";

const BTN_BASE =
  "inline-flex items-center justify-center gap-2 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed focus-visible:focus-ring";
const BTN_VARIANT: Record<ButtonVariant, string> = {
  primary: "accent-gradient text-black hover:brightness-110 shadow-panel",
  secondary: "border border-border bg-surface-2 text-text hover:bg-border/60",
  ghost: "text-muted hover:text-text hover:bg-surface-2",
  danger: "border border-danger/40 text-danger hover:bg-danger/10",
};
const BTN_SIZE: Record<ButtonSize, string> = {
  sm: "h-8 px-3 text-xs",
  md: "h-10 px-4 text-sm",
  lg: "h-12 px-6 text-base",
};

export function Button({
  variant = "secondary",
  size = "md",
  icon,
  className,
  children,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: ButtonVariant; size?: ButtonSize; icon?: string }) {
  return (
    <button className={cx(BTN_BASE, BTN_VARIANT[variant], BTN_SIZE[size], className)} {...rest}>
      {icon && <Icon name={icon} size={size === "lg" ? 20 : 16} />}
      {children}
    </button>
  );
}

// ── Card / Panel ────────────────────────────────────────────────────────────────
export function Card({ className, children }: { className?: string; children: ReactNode }) {
  return <div className={cx("panel p-4", className)}>{children}</div>;
}

// ── Form-Feld (Label + Beschreibung) ────────────────────────────────────────────
// WICHTIG: Container ist ein <div>, KEIN <label>. Unsere Controls sind teils <button>
// (Toggle/Segmented/Stepper) — ein umschließendes <label> assoziiert sich mit dem ERSTEN
// klickbaren Control, sodass ein Klick auf den Feld-Titel/Leerraum diesen Button ungewollt
// auslöst (sichtbar als „verbuggte" Switches). Für echte Label↔Input-Assoziation kann
// `htmlFor` (+ passende `id` am Control) gesetzt werden → rendert dann ein <label>.
export function Field({
  label,
  htmlFor,
  children,
}: {
  label: string;
  htmlFor?: string;
  children: ReactNode;
}) {
  return (
    <div className="block space-y-1.5">
      {htmlFor ? (
        <label htmlFor={htmlFor} className="block text-xs font-medium uppercase tracking-wide text-muted">
          {label}
        </label>
      ) : (
        <span className="block text-xs font-medium uppercase tracking-wide text-muted">{label}</span>
      )}
      {children}
    </div>
  );
}

const INPUT_BASE =
  "w-full rounded-lg border border-border bg-surface-2 px-3 py-2 text-sm text-text placeholder:text-muted/60 focus-visible:focus-ring";

export function TextInput(props: InputHTMLAttributes<HTMLInputElement>) {
  return <input {...props} className={cx(INPUT_BASE, props.className)} />;
}

export function TextArea(props: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea {...props} className={cx(INPUT_BASE, "min-h-[88px] resize-y leading-relaxed", props.className)} />;
}

export function Select({ children, ...props }: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select {...props} className={cx(INPUT_BASE, "appearance-none pr-8", props.className)}>
      {children}
    </select>
  );
}

// ── Segmented (kleine Auswahl, z.B. Aspect-Ratio) ───────────────────────────────
export function Segmented<T extends string>({
  value,
  options,
  onChange,
}: {
  value: T;
  options: { value: T; label: string }[];
  onChange: (v: T) => void;
}) {
  return (
    <div className="flex flex-wrap gap-1 rounded-lg border border-border bg-surface-2 p-1">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          onClick={() => onChange(o.value)}
          className={cx(
            "flex-1 rounded-md px-2.5 py-1.5 text-xs font-medium transition-colors",
            o.value === value ? "bg-accent/20 text-accent" : "text-muted hover:text-text",
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

// ── Toggle ──────────────────────────────────────────────────────────────────────
export function Toggle({
  checked,
  onChange,
  label,
  ariaLabel,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label?: string;
  /** Programmatisches Label für Switches ohne sichtbaren Text (Screenreader/Keyboard). */
  ariaLabel?: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel}
      onClick={() => onChange(!checked)}
      className="inline-flex items-center gap-2.5"
    >
      <span
        className={cx(
          "relative inline-block h-5 w-9 shrink-0 rounded-full transition-colors",
          checked ? "bg-accent" : "bg-border",
        )}
      >
        <span
          className={cx(
            "absolute left-0.5 top-1/2 h-4 w-4 -translate-y-1/2 rounded-full bg-white shadow-sm transition-transform",
            checked ? "translate-x-4" : "translate-x-0",
          )}
        />
      </span>
      {label && <span className="text-sm text-text">{label}</span>}
    </button>
  );
}

// ── Stepper (Ganzzahl mit −/+) ────────────────────────────────────────────────
export function Stepper({
  value,
  onChange,
  min,
  max,
  step = 1,
  suffix,
}: {
  value: number;
  onChange: (v: number) => void;
  min?: number;
  max?: number;
  step?: number;
  suffix?: string;
}) {
  const clamp = (n: number) => Math.min(max ?? Infinity, Math.max(min ?? -Infinity, n));
  return (
    <div className="inline-flex h-10 items-center rounded-lg border border-border bg-surface-2">
      <button type="button" className="grid h-full w-9 place-items-center text-muted hover:text-text" onClick={() => onChange(clamp(value - step))} aria-label="weniger">
        <Icon name="minus" size={16} />
      </button>
      <span className="min-w-[3.5rem] text-center text-sm tabular-nums">
        {value}
        {suffix ? <span className="text-muted"> {suffix}</span> : null}
      </span>
      <button type="button" className="grid h-full w-9 place-items-center text-muted hover:text-text" onClick={() => onChange(clamp(value + step))} aria-label="mehr">
        <Icon name="plus" size={16} />
      </button>
    </div>
  );
}

// ── Slider ──────────────────────────────────────────────────────────────────────
export function Slider({
  value,
  onChange,
  min = 0,
  max = 100,
  step = 1,
}: {
  value: number;
  onChange: (v: number) => void;
  min?: number;
  max?: number;
  step?: number;
}) {
  return (
    <div className="flex items-center gap-3">
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="h-1.5 w-full cursor-pointer appearance-none rounded-full bg-border accent-accent"
      />
      <span className="w-10 text-right text-sm tabular-nums text-muted">{value}</span>
    </div>
  );
}

// ── Badge ────────────────────────────────────────────────────────────────────────
export function Badge({
  children,
  tone = "neutral",
}: {
  children: ReactNode;
  tone?: "neutral" | "accent" | "warn" | "danger";
}) {
  const tones: Record<string, string> = {
    neutral: "border-border bg-surface-2 text-muted",
    accent: "border-accent/30 bg-accent/15 text-accent",
    warn: "border-amber-500/30 bg-amber-500/10 text-amber-400",
    danger: "border-danger/30 bg-danger/10 text-danger",
  };
  return (
    <span className={cx("inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium", tones[tone])}>
      {children}
    </span>
  );
}

// ── Spinner ──────────────────────────────────────────────────────────────────────
export function Spinner({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" className="animate-spin text-accent" aria-label="lädt">
      <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeOpacity="0.2" strokeWidth="3" />
      <path d="M21 12a9 9 0 0 0-9-9" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}

// ── ProgressBar ────────────────────────────────────────────────────────────────────
// D169: ein wiederverwendbarer Fortschrittsbalken (§11). Vereinheitlicht die D147-Job-
// Step-Optik (wandernder Gradient-Glanz) und kennt zwei Modi:
//  • determinate — `value` gesetzt ⇒ Füllung auf value/max % (geklemmt auf [0, max]),
//    monoton via `transition-[width]`; für zählbare Phasen (i von n).
//  • indeterminate — `value` weggelassen ⇒ ein schmales Segment läuft endlos durch den
//    Track; für einen einzelnen Schritt unbekannter Restdauer (Netzwerk/Boot).
// Größe: `sm` = h-1 (wie die Jobs-Liste), `md` = h-1.5. `label` setzt das aria-label.
export function ProgressBar({
  value,
  max = 100,
  label,
  size = "sm",
  className,
}: {
  value?: number;
  max?: number;
  label?: string;
  size?: "sm" | "md";
  className?: string;
}) {
  const indeterminate = value === undefined;
  const safeMax = Math.max(1, max);
  const pct = indeterminate ? 0 : Math.round((Math.max(0, Math.min(value, safeMax)) / safeMax) * 100);
  return (
    <div
      className={cx("relative w-full overflow-hidden rounded-full bg-surface-2", size === "md" ? "h-1.5" : "h-1", className)}
      role="progressbar"
      aria-label={label}
      aria-valuemin={indeterminate ? undefined : 0}
      aria-valuemax={indeterminate ? undefined : 100}
      aria-valuenow={indeterminate ? undefined : pct}
      aria-busy={indeterminate || undefined}
    >
      {indeterminate ? (
        // Statischer Gradient + Lauf-Animation (kein Shimmer — beide setzen `animation`, das
        // würde sich gegenseitig überschreiben). Das Segment wandert links→rechts durch den Track.
        <div className="absolute inset-y-0 w-2/5 rounded-full bg-gradient-to-r from-accent via-accent-2 to-accent animate-progress-indeterminate" />
      ) : (
        <div
          className="h-full rounded-full bg-gradient-to-r from-accent via-accent-2 to-accent bg-[length:200%_100%] animate-shimmer transition-[width] duration-500 ease-out"
          style={{ width: `${pct}%` }}
        />
      )}
    </div>
  );
}

export { cx };
